import React, { useEffect, useMemo } from "react";
import { BrowserRouter } from "react-router-dom";
import PublicRouter from "./PublicRouter";
import PrivateRouter from "./PrivateRouter";
import { DEFAULT_APP_VERSION } from "../constants/login";

type Props = {
	variant: string;
};

function normalizeVariant(v: string) {
	return (v || DEFAULT_APP_VERSION).replace(/^\/+/, "").toLowerCase();
}

function getPrefixVariantFromPathname(pathname: string): string | null {
	const first = pathname.split("/")[1];
	if (/^v\d+$/i.test(first)) return first.toLowerCase();
	return null;
}

function stripLeadingVariantPrefix(pathname: string): string {
	return pathname.replace(/^\/v\d+/i, "") || "/";
}

function isPublicPathname(pathname: string): boolean {
	const p = pathname.toLowerCase();

	return (
		p === "/login" ||
		p.startsWith("/login/") ||
		p === "/login-offline" ||
		p.startsWith("/login-offline/") ||
		p === "/terms" ||
		p.startsWith("/terms/") ||
		p === "" ||
		p === "/"
	);
}

export default function RootRouter({ variant }: Props) {
	const v = useMemo(() => normalizeVariant(variant) || DEFAULT_APP_VERSION, [variant]);

	const pathname = window.location.pathname;
	const urlPrefix = getPrefixVariantFromPathname(pathname);
	const pathWithoutPrefix = urlPrefix ? stripLeadingVariantPrefix(pathname) : pathname;

	const isPublic = isPublicPathname(pathWithoutPrefix);

	useEffect(() => {
		if (isPublic && urlPrefix) {
			const newPath = pathWithoutPrefix;
			window.location.replace(`${newPath}${window.location.search}${window.location.hash}`);
			return;
		}

		if (!isPublic) {
			if (!urlPrefix) {
				const newPath = `/${v}${pathname.startsWith("/") ? "" : "/"}${pathname}`;
				window.location.replace(`${newPath}${window.location.search}${window.location.hash}`);
				return;
			}

			if (urlPrefix !== v) {
				const rest = stripLeadingVariantPrefix(pathname);
				const newPath = `/${v}${rest === "/" ? "" : rest}`;
				window.location.replace(`${newPath}${window.location.search}${window.location.hash}`);
				// eslint-disable-next-line no-useless-return
				return;
			}
		}
	}, [isPublic, urlPrefix, v, pathname, pathWithoutPrefix]);

	const isRedirectNeeded = (isPublic && !!urlPrefix) || (!isPublic && (!urlPrefix || urlPrefix !== v));

	if (isRedirectNeeded) return null;

	if (isPublic) {
		return (
			<BrowserRouter>
				<PublicRouter />
			</BrowserRouter>
		);
	}

	if (process.env.REACT_APP_VERSION === "v2" || process.env.REACT_APP_VERSION === "v1" || process.env.REACT_APP_ENVIRONMENT === "OFFLINE") {
		return (
			<BrowserRouter basename={`/${v}`}>
				<PrivateRouter />
			</BrowserRouter>
		);
	}
	// }else 	if (process.env.REACT_APP_ENVIRONMENT === "OFFLINE") {
	// 	return (
	// 		<BrowserRouter >
	// 			<PrivateRouter />
	// 		</BrowserRouter>
	// 	);
	// }

	return null;
}
